import { useEffect, useState, type ReactNode } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { array, object, string } from 'yup';
import { Formik, getIn } from 'formik';
import { useNavigate } from 'react-router-dom';

import Select, { type SingleValue } from 'react-select';
import OptionLayoutPlace from '@/app/common/components/OptionsLayout/OptionLayoutPlace';
import SingleValueFlag from '@/app/common/components/OptionsLayout/SingleValueFlag';
import { useDispatch } from 'react-redux';
import OptionLayoutIcon from '@/app/common/components/OptionsLayout/OptionLayoutIcon';
import SingleValueIcon from '@/app/common/components/OptionsLayout/SingleValueIcon';
import analysisApi, {
  useCreateAnalysisMutation,
  useCreateAnalysisScheduleMutation,
  useGetAnalysisScheduleQuery,
  useUpdateAnalysisScheduleMutation
} from '@/app/PageSpeed/api/analysisApi';
import { errorMessage, successMessage, warningMessage } from '@/utils/toast';
import _ from 'lodash';
import AnalysisProgressBar from './AnalysisProgressBar';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';
import classNames from 'classnames';
import useDomain from '@/hooks/useDomain';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import type {
  ActiveAnalysisJob,
  AnalysisSchedule,
  AnalysisScheduleInput,
  AnalysisSettings
} from '@/app/PageSpeed/api/types/analysisApiTypes';
import { getAnalysisTaskIdFromCreateResponse } from '@/app/PageSpeed/api/types/analysisApiTypes';
import AnalysisScheduleDrawer, {
  type AnalysisScheduleConfig
} from './AnalysisScheduleDrawer';
import {
  analysisSelectStyles,
  deviceOptions,
  internetOptions,
  internetSelectOptions,
  placeOptions,
  type DeviceOption,
  type InternetOption,
  type PlaceOption
} from '@/app/PageSpeed/config/analysisFormOptions';
import useAnalysisPlaceOptions from '@/app/PageSpeed/hooks/useAnalysisPlaceOptions';

type FormValues = { urls: string[] };
type AddressMode = 'single' | 'multiple';
type AddressModeOption = {
  value: AddressMode;
  label: string;
};

const addressModeOptions: AddressModeOption[] = [
  { value: 'single', label: 'یک آدرس' },
  { value: 'multiple', label: 'چند آدرس' }
];

const getInactiveLocationReason = (response: unknown): string | null => {
  if (!response || typeof response !== 'object') return null;

  const payload = response as {
    code?: unknown;
    data?: unknown;
  };
  if (payload.code === 40010 && payload.data && typeof payload.data === 'object') {
    const reason = (payload.data as { reason?: unknown }).reason;
    return typeof reason === 'string' ? reason : null;
  }

  return getInactiveLocationReason(payload.data);
};

type Props = {
  initialSettings?: AnalysisSettings | Record<string, unknown>;
  /** Controlled active job (e.g. History page + grid re-run) */
  activeAnalysis?: ActiveAnalysisJob | null;
  onActiveAnalysisChange?: (job: ActiveAnalysisJob | null) => void;
  endAction?: ReactNode;
  onScheduleSave?: (config: AnalysisScheduleConfig) => void;
};

const NewAnalysisForm = ({
  initialSettings = {},
  activeAnalysis: controlledActiveAnalysis,
  onActiveAnalysisChange,
  endAction,
  onScheduleSave
}: Props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { domain, domain_slug } = useDomain();
  const isLgUp = useMediaQuery(breakpointsMediaQuery.lg);
  const [internalActiveAnalysis, setInternalActiveAnalysis] =
    useState<ActiveAnalysisJob | null>(null);
  const activeAnalysis =
    controlledActiveAnalysis !== undefined
      ? controlledActiveAnalysis
      : internalActiveAnalysis;
  const setActiveAnalysis =
    onActiveAnalysisChange ?? setInternalActiveAnalysis;

  const [selectedPlace, setSelectedPlace] = useState<PlaceOption | undefined>(
    _.isEmpty(initialSettings)
      ? placeOptions[0]
      : placeOptions.find(
          item => item.value === (initialSettings as AnalysisSettings).location_code
        )
  );
  const [selectedDevice, setSelectedDevice] = useState<
    DeviceOption | undefined
  >(
    _.isEmpty(initialSettings)
      ? {
          value: 'mobile',
          label: 'گوشی',
          icon: 'mobile-alt'
        }
      : deviceOptions.find(
          item => item.value === (initialSettings as AnalysisSettings).device_type
        )
  );
  const internetFromSettings = internetOptions.find(
    item => item === (initialSettings as AnalysisSettings).internet_type
  );
  const [selectedInternet, setSelectedInternet] = useState<InternetOption>(
    _.isEmpty(initialSettings)
      ? {
          value: 'wifi',
          label: 'WIFI'
        }
      : {
          label: (internetFromSettings || 'wifi').toUpperCase(),
          value: internetFromSettings || 'wifi'
        }
  );
  const [createAnalysis, { isLoading: isCreatingAnalysis }] =
    useCreateAnalysisMutation();
  const [createAnalysisError, setCreateAnalysisError] = useState<string | null>(
    null
  );
  const [addressMode, setAddressMode] = useState<AddressMode>('single');
  const placeSelectOptions = useAnalysisPlaceOptions();
  const { data: scheduleResponse } = useGetAnalysisScheduleQuery();
  const [createSchedule] = useCreateAnalysisScheduleMutation();
  const [updateSchedule] = useUpdateAnalysisScheduleMutation();
  const scheduleData = scheduleResponse?.data;
  const existingSchedule: AnalysisSchedule | undefined = Array.isArray(
    scheduleData
  )
    ? scheduleData[0]
    : scheduleData &&
        typeof scheduleData === 'object' &&
        'rows' in scheduleData
      ? scheduleData.rows[0]
      : (scheduleData as AnalysisSchedule | undefined);

  useEffect(() => {
    const current = placeSelectOptions.find(
      option => option.value === selectedPlace?.value
    );
    if (current && !current.isDisabled) return;
    const firstActive = placeSelectOptions.find(option => !option.isDisabled);
    if (firstActive) {
      setSelectedPlace(firstActive);
    }
  }, [placeSelectOptions, selectedPlace?.value]);
  const selectedPlaceIsDisabled = Boolean(
    placeSelectOptions.find(option => option.value === selectedPlace?.value)
      ?.isDisabled
  );
  const [showScheduleDrawer, setShowScheduleDrawer] = useState(false);
  const handleScheduleSave = async (config: AnalysisScheduleConfig) => {
    const body: AnalysisScheduleInput = {
      urls: config.entries,
      notification_method:
        config.notification_method.length === 1
          ? config.notification_method[0]
          : config.notification_method,
      scheduled_day: config.day,
      enabled: config.enabled
    };
    try {
      if (existingSchedule?.id) {
        await updateSchedule({ id: existingSchedule.id, ...body }).unwrap();
      } else {
        await createSchedule(body).unwrap();
      }
      successMessage('تنظیمات زمان‌بندی ذخیره شد');
      setShowScheduleDrawer(false);
    } catch (error) {
      errorMessage('خطایی در ذخیره زمان‌بندی وجود دارد');
      console.error(error);
    }
  };
  const onSubmit = async (values: FormValues) => {
    if (!selectedPlace || !selectedDevice) return;
    if (selectedPlaceIsDisabled) return;
    const urls = values.urls.map(url => url.trim()).filter(Boolean);
    if (!urls.length) return;

    setCreateAnalysisError(null);
    createAnalysis({
      body: {
        url: urls,
        location_code: selectedPlace.value,
        device_type: selectedDevice.value,
        internet_type: selectedInternet.value
      },
      country: selectedPlace.value
    })
      .unwrap()
      .then(res => {
        const inactiveLocationReason = getInactiveLocationReason(res);
        if (inactiveLocationReason) {
          setCreateAnalysisError(inactiveLocationReason);
          return;
        }

        const isOk =
          res?.success === true || res?.code === 200 || res?.code === 201;
        if (isOk) {
          const responseJobId =
            res.data && typeof res.data !== 'string' ? res.data.job_id : undefined;
          const progressId =
            responseJobId || getAnalysisTaskIdFromCreateResponse(res);
          if (progressId) {
            setActiveAnalysis({
              jobId: progressId,
              url: urls.join(' • ')
            });
          }
          dispatch(analysisApi.util.invalidateTags(['AnalysisHistory']));
          successMessage(
            'درخواست واکاوی صفحه ارسال شد، بعد از ۶۰ ثانیه نتیجه در جدول زیر نمایش داده میشه.'
          );
        } else {
          if (res.code == 40001) {
            warningMessage(
              (
                <span>
                  هر دقیقه یکبار میتونی یک صفحه رو واکاوی کنی (<b>{String(res.data)}</b>{' '}
                  ثانیه دیگه مجددا میتونی واکاوی انجام بدی)
                </span>
              ) as ReactNode
            );
          }
        }
      })
      .catch(err => {
        console.error(err);
        const inactiveLocationReason = getInactiveLocationReason(err);
        if (inactiveLocationReason) {
          setCreateAnalysisError(inactiveLocationReason);
          return;
        }
        errorMessage('ارسال درخواست با خطا مواجه شد لطفا مجددا تلاش کنید.');
      });
  };
  const selectStyles = analysisSelectStyles;

  return (
    <>
      <Formik
        onSubmit={onSubmit}
        initialValues={{
          urls: [(initialSettings as AnalysisSettings)?.url || '']
        }}
        validationSchema={object().shape({
          urls: array()
            .max(3, 'حداکثر ۳ آدرس می‌توانید وارد کنید')
            .of(
              string().url(
                'آدرس معتبر نیست (باید با http یا https شروع بشه).'
              )
            )
            .test(
              'urls',
              'حداقل یک آدرس وارد کنید',
              value =>
                Array.isArray(value) &&
                value.some(url => typeof url === 'string' && url.trim())
            )
        })}
        enableReinitialize
      >
        {({
          values,
          errors,
          handleBlur,
          touched,
          handleSubmit,
          setFieldValue
        }) => (
          <div className="w-100 w-lg-100 mt-3 mb-2 position-relative">
            <Form onSubmit={handleSubmit}>
              <Form.Group>
                <div
                  className={classNames('d-flex w-100', {
                    'flex-column': !isLgUp,
                    'align-items-start flex-wrap': isLgUp,
                    'gap-1': isLgUp,
                    'gap-2': !isLgUp
                  })}
                >
                  <div
                    className={classNames('flex-shrink-0', {
                      'd-flex': isLgUp,
                      'gap-1': isLgUp,
                      'd-flex flex-column gap-2 w-100': !isLgUp
                    })}
                  >
                    <div style={{ width: isLgUp ? '160px' : '100%' }}>
                      <Select<PlaceOption>
                        menuShouldScrollIntoView={true}
                        closeMenuOnSelect={true}
                        isSearchable={true}
                        onChange={(e: SingleValue<PlaceOption>) => {
                          if (e && !e.isDisabled) setSelectedPlace(e);
                        }}
                        options={placeSelectOptions}
                        isOptionDisabled={option => Boolean(option.isDisabled)}
                        classNamePrefix="react-select"
                        value={
                          placeSelectOptions.find(
                            option => option.value === selectedPlace?.value
                          ) || selectedPlace
                        }
                        maxMenuHeight={300}
                        styles={selectStyles('5px')}
                        components={{
                          Option: OptionLayoutPlace as never,
                          SingleValue: SingleValueFlag as never
                        }}
                      />
                    </div>
                    <div style={{ width: isLgUp ? '120px' : '100%' }}>
                      <Select<DeviceOption>
                        menuShouldScrollIntoView={true}
                        closeMenuOnSelect={true}
                        isSearchable={true}
                        onChange={(e: SingleValue<DeviceOption>) => {
                          if (e) setSelectedDevice(e);
                        }}
                        options={deviceOptions}
                        classNamePrefix="react-select"
                        value={selectedDevice}
                        maxMenuHeight={300}
                        styles={selectStyles('5px')}
                        components={{
                          Option: OptionLayoutIcon as never,
                          SingleValue: SingleValueIcon as never
                        }}
                      />
                    </div>
                    <div style={{ width: isLgUp ? '120px' : '100%' }}>
                      <Select<InternetOption>
                        menuShouldScrollIntoView={true}
                        closeMenuOnSelect={true}
                        isSearchable={true}
                        onChange={(e: SingleValue<InternetOption>) => {
                          if (e) setSelectedInternet(e);
                        }}
                        options={internetSelectOptions}
                        classNamePrefix="react-select"
                        value={selectedInternet}
                        maxMenuHeight={300}
                        styles={selectStyles('5px')}
                        components={{
                          Option: OptionLayoutIcon as never,
                          SingleValue: SingleValueIcon as never
                        }}
                      />
                    </div>
                  </div>

                  <div
                    className="flex-grow-1 min-w-0 d-flex flex-column gap-2"
                    style={isLgUp ? { flex: '1 1 28rem' } : undefined}
                  >
                    <div
                      className={classNames('d-flex gap-2', {
                        'flex-column align-items-stretch': !isLgUp,
                        'align-items-start': isLgUp
                      })}
                    >
                      <div style={{ width: isLgUp ? '165px' : '100%' }}>
                        <Select<AddressModeOption>
                          menuShouldScrollIntoView={true}
                          closeMenuOnSelect={true}
                          isSearchable={false}
                          onChange={option => {
                            if (!option) return;
                            setAddressMode(option.value);
                            if (option.value === 'single') {
                              setFieldValue('urls', [values.urls[0] || '']);
                            } else {
                              setFieldValue('urls', values.urls.slice(0, 3));
                            }
                          }}
                          options={addressModeOptions}
                          classNamePrefix="react-select"
                          value={addressModeOptions.find(
                            option => option.value === addressMode
                          )}
                          styles={selectStyles('5px')}
                          components={{
                            Option: OptionLayoutIcon as never,
                            SingleValue: SingleValueIcon as never
                          }}
                        />
                      </div>
                      <div
                      className={classNames('min-w-0', {
                        'd-flex flex-wrap align-items-start gap-1': isLgUp,
                        'd-flex flex-column gap-2 flex-grow-1': !isLgUp,
                        'flex-grow-1': isLgUp
                      })}
                      >
                      {addressMode === 'multiple' ? (
                        <Form.Control
                          as="textarea"
                          rows={3}
                          className={classNames({
                            'flex-grow-1': addressMode === 'multiple' && isLgUp,
                            'w-100': !isLgUp
                          })}
                          style={{
                            direction: 'ltr',
                            resize: 'vertical',
                            minHeight: 86,
                            minWidth: 0,
                            borderRadius: 5
                          }}
                          placeholder="هر آدرس را در یک سطر وارد کنید (حداکثر ۳ آدرس)"
                          value={values.urls.join('\n')}
                          onChange={event =>
                            setFieldValue(
                              'urls',
                              event.target.value.split(/\r?\n/).slice(0, 3)
                            )
                          }
                          onBlur={handleBlur}
                          name="urls"
                          isInvalid={Boolean(getIn(touched, 'urls') && errors.urls)}
                        />
                      ) : (
                        <Form.Control
                          isInvalid={Boolean(
                            getIn(touched, 'urls[0]') &&
                              getIn(errors, 'urls[0]')
                          )}
                          isValid={Boolean(
                            getIn(touched, 'urls[0]') &&
                              !getIn(errors, 'urls[0]') &&
                              values.urls[0]
                          )}
                          type="text"
                          className="rounded flex-grow-1"
                          style={{
                            direction: values.urls[0]?.length ? 'ltr' : 'rtl',
                            minWidth: 0,
                            flex: isLgUp ? '1 1 12rem' : undefined
                          }}
                          placeholder="نشانی شامل https یا http باشد"
                          value={values.urls[0] || ''}
                          onChange={event =>
                            setFieldValue('urls', [event.target.value])
                          }
                          onBlur={handleBlur}
                          name="urls[0]"
                        />
                      )}
                      <div
                        className={classNames(
                          'd-flex align-items-start gap-1 flex-shrink-0',
                          { 'w-100': !isLgUp }
                        )}
                      >
                      <Button
                        style={{
                          borderRadius: 5,
                          width: isLgUp ? 120 : undefined,
                          height: 38
                        }}
                        variant="falcon-primary"
                        className={classNames(
                          'justify-content-center d-flex align-items-center gap-2 flex-shrink-0',
                          { 'flex-grow-1': !isLgUp }
                        )}
                        type="submit"
                        disabled={isCreatingAnalysis || selectedPlaceIsDisabled}
                      >
                        {isCreatingAnalysis ? (
                          <>
                            <Spinner style={{ width: '16px', height: '16px' }} />
                            درحال بررسی
                          </>
                        ) : (
                          <>بررسی</>
                        )}
                      </Button>
                      <ChildrensTooltip
                        placement="top"
                        html="تنظیم بررسی خودکار سرعت و ارسال نتیجه در پیام‌رسان‌ها"
                        noChildSpan
                      >
                        <Button
                          type="button"
                          variant="outline-secondary"
                          className="d-inline-flex align-items-center justify-content-center p-0 flex-shrink-0"
                          style={{
                            width: 38,
                            height: 38,
                            padding: 0,
                            lineHeight: 1
                          }}
                          aria-label="زمان‌بندی بررسی سرعت"
                          onClick={() => setShowScheduleDrawer(true)}
                        >
                          <FontAwesomeIcon
                            icon="clock"
                            fixedWidth
                            style={{
                              width: 16,
                              height: 16,
                              margin: 0,
                              lineHeight: 1,
                              verticalAlign: 'middle'
                            }}
                          />
                        </Button>
                      </ChildrensTooltip>
                      </div>
                      {endAction}
                      </div>
                    </div>
                  </div>
                </div>
                {typeof errors.urls === 'string' ? (
                  <Form.Control.Feedback className="d-block" type="invalid">
                    &nbsp; {errors.urls}
                  </Form.Control.Feedback>
                ) : (
                  values.urls.some((_, index) => {
                    const fieldError = getIn(errors, `urls[${index}]`);
                    const fieldTouched = getIn(touched, `urls[${index}]`);
                    return fieldTouched && fieldError;
                  }) && (
                    <Form.Control.Feedback className="d-block" type="invalid">
                      &nbsp;
                      {values.urls
                        .map((_, index) => getIn(errors, `urls[${index}]`))
                        .find(Boolean)}
                    </Form.Control.Feedback>
                  )
                )}
              </Form.Group>
            </Form>
            {(selectedPlace?.value === 'google' ||
              selectedPlace?.value === 'not_ir') && (
              <Alert variant="warning" className="mt-2 mb-0 w-100 fs--1">
                به دلیل اختلال اینترنت بین‌الملل و محدودیت‌های موجود در سرورهای
                ایران ممکن است در زمان بررسی شما پاسخی دریافت نشود.
              </Alert>
            )}
            {createAnalysisError && (
              <Alert variant="danger" className="mt-2 mb-0 w-100 fs--1">
                {createAnalysisError}
              </Alert>
            )}
            <AnalysisScheduleDrawer
              show={showScheduleDrawer}
              initialUrls={values.urls}
              initialSettings={{
                location_code: selectedPlace?.value,
                device_type: selectedDevice?.value,
                internet_type: selectedInternet.value
              }}
              initialSchedule={existingSchedule}
              onHide={() => setShowScheduleDrawer(false)}
              onSave={onScheduleSave ?? handleScheduleSave}
            />
          </div>
        )}
      </Formik>
      {activeAnalysis?.jobId && (
        <AnalysisProgressBar
          key={activeAnalysis.jobId}
          url={activeAnalysis.url}
          job_id={activeAnalysis.jobId}
          onComplete={() => {
            setActiveAnalysis(null);
            if (domain_slug && domain) {
              navigate(`/analysis/overview/last/${domain_slug}/${domain}`);
            }
          }}
        />
      )}
    </>
  );
};

export default NewAnalysisForm;
